import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Gamepad2, Gift, Shield, Zap, Trophy, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WelcomePopup = ({ onClose }) => {
  const navigate = useNavigate();
  const [closing, setClosing] = useState(false);

  const handleClose = async () => {
    setClosing(true);
    try {
      // Mark popup as seen in backend
      await axios.post(`${API}/auth/welcome-popup-seen`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Error marking welcome popup as seen:', error);
    }
    setTimeout(() => {
      onClose?.();
    }, 200);
  };

  const handleCreateMatch = async () => {
    await handleClose();
    navigate('/create-match');
  };

  return (
    <AnimatePresence>
      {!closing && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-[#0A0A0B] border border-white/10 rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="welcome-popup"
          >
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors z-10"
              data-testid="close-welcome-popup"
            >
              <X className="w-5 h-5 text-white/60" />
            </button>

            {/* Header */}
            <div className="bg-gradient-to-r from-[#7000FF] to-[#00F0FF] p-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                <Gamepad2 className="w-8 h-8 text-white" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-white">
                Welcome to MatchStake 🎮
              </h2>
            </div>

            {/* Content */}
            <div className="p-6 space-y-5">
              <p className="text-white/80">
                You can now challenge anyone head-to-head in skill-based matches — from Madden and NBA 2K to chess, cornhole, fitness challenges, or anything you and your opponent agree on.
              </p>

              {/* How to Get Started */}
              <div className="space-y-3">
                <h3 className="font-semibold text-white">Here's how to get started:</h3>
                <div className="space-y-2">
                  {[
                    { num: '1️⃣', text: 'Create or accept a challenge' },
                    { num: '2️⃣', text: 'Play your match' },
                    { num: '3️⃣', text: 'Submit proof of the result' },
                    { num: '4️⃣', text: 'Winner receives the prize' }
                  ].map((step, i) => (
                    <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-white/5">
                      <span className="text-lg">{step.num}</span>
                      <span className="text-white/70">{step.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bonus */}
              <div className="p-4 rounded-xl bg-gradient-to-r from-[#CCFF00]/20 to-[#00FF94]/20 border border-[#CCFF00]/30">
                <div className="flex items-center gap-3 mb-2">
                  <Gift className="w-6 h-6 text-[#CCFF00]" />
                  <span className="font-bold text-[#CCFF00]">Play your first match and earn a $5 bonus</span>
                </div>
                <p className="text-white/70 text-sm">
                  Deposit $5 and receive a $5 bonus after your first completed match.
                </p>
              </div>

              {/* Trust Features */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: Shield, text: 'Secure match escrow', color: '#00FF94' },
                  { icon: Zap, text: 'Verified results', color: '#00F0FF' },
                  { icon: Trophy, text: 'Fast withdrawals', color: '#CCFF00' },
                  { icon: Gamepad2, text: 'Head-to-head matches', color: '#7000FF' }
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-white/5">
                    <feature.icon className="w-4 h-4" style={{ color: feature.color }} />
                    <span className="text-white/70 text-sm">{feature.text}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="space-y-3 pt-2">
                <p className="text-center text-white/60 text-sm">
                  Ready to start your first challenge?
                </p>
                <Button
                  onClick={handleCreateMatch}
                  className="w-full bg-[#00FF94] text-black hover:bg-[#00FF94]/90 font-bold py-3"
                  data-testid="welcome-create-match-btn"
                >
                  Create Match
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  onClick={handleClose}
                  variant="outline"
                  className="w-full border-white/20 text-white/60 hover:bg-white/5"
                  data-testid="welcome-later-btn"
                >
                  I'll explore first
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WelcomePopup;
